import { computed, Directive, ElementRef, HostBinding, HostListener, inject, input } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { SearchBarStore } from './search-bar.store';

export const optionId = (listboxId: string, index: number) => `${listboxId}-option-${index}`;

@Directive({
  selector: 'input[sharrySearchInput]',
  standalone: true,
})
export class SearchInputDirective {
  readonly store = inject(SearchBarStore);
  private readonly document = inject(DOCUMENT);
  private readonly element: HTMLInputElement = inject(ElementRef).nativeElement;

  readonly listboxId = input.required<string>({ alias: 'sharrySearchInput' });

  readonly activeDescendant = computed(() => {
    const index = this.store.activeIndex();
    return this.store.isOpen() && index >= 0 ? optionId(this.listboxId(), index) : null;
  });

  @HostBinding('attr.role') readonly role = 'combobox';
  @HostBinding('attr.type') readonly type = 'search';
  @HostBinding('attr.enterkeyhint') readonly enterKeyHint = 'search';
  @HostBinding('attr.autocomplete') readonly autocomplete = 'off';
  @HostBinding('attr.autocapitalize') readonly autocapitalize = 'off';
  @HostBinding('attr.spellcheck') readonly spellcheck = 'false';
  @HostBinding('attr.aria-autocomplete') readonly ariaAutocomplete = 'list';

  @HostBinding('attr.aria-expanded') get ariaExpanded() {
    return this.store.isOpen() && this.store.hasTerm();
  }

  @HostBinding('attr.aria-controls') get ariaControls() {
    return this.store.isOpen() && this.store.hasTerm() ? this.listboxId() : null;
  }

  @HostBinding('attr.aria-activedescendant') get ariaActiveDescendant() {
    return this.activeDescendant();
  }

  @HostBinding('value') get value() {
    return this.store.query();
  }

  @HostListener('input') onInput() {
    this.store.setQuery(this.element.value);
  }

  @HostListener('click') onClick() {
    this.store.open();
  }

  @HostListener('focus') onFocus() {
    if (this.skipNextFocus) {
      this.skipNextFocus = false;
      return;
    }
    this.store.open();
  }

  focusQuietly() {
    this.skipNextFocus = true;
    this.element.focus();
  }

  private skipNextFocus = false;

  @HostListener('keydown', ['$event']) onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.store.open();
        this.store.moveActive(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter': {
        event.preventDefault();
        const active = this.activeDescendant();
        if (active) {
          this.document.getElementById(active)?.click();
        } else if (this.store.canSubmit()) {
          this.store.submit();
        } else {
          break;
        }
        this.element.blur();
        break;
      }
      case 'Escape':
        if (this.store.isOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.store.dismiss();
        }
        break;
    }
  }
}
